import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Field, Icon, Select, Spinner, TextArea, useLayout, useToast } from "@agentsaas/ui";
import type { Actor, Agent, Asset, Model3dMeta } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatBytes, formatDateTime, formatMicroUsd } from "../app/format";
import { useAgents } from "../data/queries";
import { ak, useAsset, useAssetUrl } from "../data/assets";
import { CollabFrame } from "../project/ProjectChrome";
import { Loading } from "../layout/Loading";
import { ImageViewer } from "./ImageViewer";
import { Stars } from "./AssetsPage";
import { defaultNotify, gridFromParams, isImageLike, isModel, stageOf, versionDiff, type Version } from "./model";
import { createViewSync, type ViewSync } from "./viewer-model";
import { ModelStats } from "./ModelStats";
import { AssetThumb } from "./AssetThumb";

const GlbViewer = lazy(() => import("./GlbViewer"));

/**
 * /p/:pid/assets/:asid[?v=版本&compare=a,b]：资产详情。
 * - 预览：图片查看器（缩放、平移、切片网格）/ GLB 查看器（three.js，懒加载）/ 其他类型给下载链接
 * - 版本时间线：prompt、provider、成本、生成者、派生关系；点选切换预览
 * - 版本对比：同一资产两个版本并排 + 字段差异
 * - 打分评论：1–5 星 + 评论，可选通知哪些 agent（默认该版本的生成者 / 美术 agent）
 * - P8：模型指标面板（AssetVersion.model，没有时用查看器现场计算的）、派生链路（沿 derivedFrom 往上追溯）、
 *   模型版本对比时两个查看器同步视角
 */
export function AssetDetailPage() {
  const { pid = "", asid = "" } = useParams();
  return (
    <CollabFrame pid={pid} section="assets">
      <AssetDetail pid={pid} asid={asid} />
    </CollabFrame>
  );
}

function useActor(pid: string) {
  const { t } = useT();
  const me = useSession().state.user;
  const agents = useAgents(pid).data ?? [];
  return (a: Actor) => (a.type === "agent" ? (agents.find((x) => x.id === a.id)?.name ?? a.id) : a.type === "user" ? (me?.id === a.id ? t("chat.you") : t("room.member_user")) : t("room.system"));
}

function AssetDetail({ pid, asid }: { pid: string; asid: string }) {
  const { t } = useT();
  const layout = useLayout();
  const asset = useAsset(asid);
  const [computed, setComputed] = useState<{ key: string; meta: Model3dMeta } | null>(null);
  // 查看器现场计算的指标：按"资产@版本"记一份（同一版本重复回调不触发重渲染）
  const statsKeyRef = useRef("");
  const onStats = useCallback((m: Model3dMeta) => {
    const key = statsKeyRef.current;
    setComputed((c) => (c?.key === key ? c : { key, meta: m }));
  }, []);
  const [sp, setSp] = useSearchParams();
  const setParam = (k: string, v: string | null) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );

  if (asset.isPending) return <Loading />;
  if (!asset.data) return <EmptyState icon="image" title={t("error.common.not_found")} />;
  const a = asset.data;
  const versions = [...a.versions].sort((x, y) => y.version - x.version);
  const selected = versions.find((v) => v.version === Number(sp.get("v"))) ?? versions[0]!;
  const compareRaw = sp.get("compare");
  const compare = compareRaw ? compareRaw.split(",").map(Number) : null;
  const model = isModel(a.kind, selected.mediaType);
  const statsKey = `${a.id}@${selected.version}`;
  statsKeyRef.current = statsKey;

  return (
    <div className="page asset-detail" data-testid="asset-detail">
      <Link to={`/p/${pid}/assets`} className="small task-detail__back" data-testid="asset-back">
        <Icon name="chevronLeft" size={14} />
        {t("asset.back")}
      </Link>
      <div className="asset-detail__head">
        <h1 className="page__title grow ellipsis" data-testid="asset-name">
          {a.name}
        </h1>
        <Badge>{t(`asset.kind.${a.kind}`)}</Badge>
        <Stars value={a.rating} label={a.rating === null ? t("asset.unrated") : t("asset.rating_label", { rating: a.rating })} />
      </div>
      {a.tags.length ? (
        <div className="asset-detail__tags">
          {a.tags.map((x) => (
            <Link key={x} to={`/p/${pid}/assets?tag=${encodeURIComponent(x)}`} className="chip">
              {x}
            </Link>
          ))}
        </div>
      ) : null}
      {compare && versions.length > 1 ? (
        <Compare asset={a} versions={versions} pair={compare} onPair={(x, y) => setParam("compare", `${x},${y}`)} onClose={() => setParam("compare", null)} pid={pid} />
      ) : (
        <div className={`asset-detail__grid${layout === "desktop" ? " is-wide" : ""}`}>
          <section className="asset-detail__preview" aria-label={t("asset.preview")}>
            <Preview asset={a} version={selected} onStats={onStats} />
            <div className="row asset-detail__actions">
              <DownloadLink asset={a} version={selected.version} />
              {versions.length > 1 ? (
                <Button size="sm" icon="compare" onClick={() => setParam("compare", `${versions[1]!.version},${versions[0]!.version}`)} data-testid="asset-compare">
                  {t("asset.compare")}
                </Button>
              ) : null}
            </div>
          </section>
          <aside className="asset-detail__side">
            {model ? <ModelPanel version={selected} computed={computed?.key === statsKey ? computed.meta : null} /> : null}
            <Lineage pid={pid} asset={a} version={selected} />
            <Timeline pid={pid} versions={versions} selected={selected.version} onSelect={(v) => setParam("v", v === versions[0]!.version ? null : String(v))} />
            <Feedback pid={pid} asset={a} version={selected} />
          </aside>
        </div>
      )}
    </div>
  );
}

function Preview({ asset, version, compact, onStats, sync }: { asset: Pick<Asset, "id" | "kind" | "name">; version: Version; compact?: boolean; onStats?(m: Model3dMeta): void; sync?: ViewSync }) {
  const { t } = useT();
  const original = useAssetUrl(asset.id, version.version, isImageLike(asset.kind, version.mediaType) ? "preview" : "original");
  const grid = useMemo(() => gridFromParams(asset.kind, version.params), [asset.kind, version.params]);
  if (original.isPending) return <Spinner label={t("common.loading")} />;
  if (!original.data) return <EmptyState icon="image" title={t("asset.preview_unavailable")} />;
  const label = `${asset.name} ${t("asset.version_short", { version: version.version })}`;
  if (isModel(asset.kind, version.mediaType))
    return (
      <Suspense fallback={<Spinner label={t("common.loading")} />}>
        <GlbViewer key={original.data.url} url={original.data.url} name={label} compact={compact} mediaType={version.mediaType} meta={version.model} onStats={onStats} sync={sync} />
      </Suspense>
    );
  if (isImageLike(asset.kind, version.mediaType)) return <ImageViewer key={original.data.url} src={original.data.url} alt={label} grid={grid} compact={compact} />;
  return <EmptyState icon="file" title={t("asset.no_preview")} />;
}

function DownloadLink({ asset, version }: { asset: Pick<Asset, "id" | "name">; version: number }) {
  const { t } = useT();
  const url = useAssetUrl(asset.id, version, "original");
  if (!url.data) return null;
  return (
    <a className="ui-btn ui-btn--secondary ui-btn--sm" href={url.data.url} download={asset.name} target="_blank" rel="noreferrer noopener" data-testid="asset-download">
      <Icon name="download" />
      {t("asset.download")}
    </a>
  );
}

function Timeline({ pid, versions, selected, onSelect }: { pid: string; versions: Version[]; selected: number; onSelect(v: number): void }) {
  const { t, locale } = useT();
  const actor = useActor(pid);
  const tz = useSession().state.user?.timezone;
  return (
    <section className="stack" aria-labelledby="versions-title">
      <h2 id="versions-title" className="section-title">
        {t("asset.versions")} <Badge>{versions.length}</Badge>
      </h2>
      <ol className="vtimeline" data-testid="version-list">
        {versions.map((v) => (
          <li key={v.version} className={`vtimeline__item${v.version === selected ? " is-active" : ""}`}>
            <button type="button" className="vtimeline__btn" aria-pressed={v.version === selected} onClick={() => onSelect(v.version)} data-testid="version-item" data-version={v.version}>
              <span className="vtimeline__head">
                <strong>{t("asset.version_short", { version: v.version })}</strong>
                <span className="small muted">{actor(v.createdBy)}</span>
                <span className="grow" />
                <time className="small muted" dateTime={v.createdAt}>
                  {formatDateTime(v.createdAt, locale, tz)}
                </time>
              </span>
              {v.prompt ? <span className="vtimeline__prompt small">{v.prompt}</span> : <span className="small muted">{t("asset.no_prompt")}</span>}
              <span className="vtimeline__meta small muted">
                {v.provider ? <span>{v.provider}</span> : null}
                <span>{t("asset.cost", { cost: formatMicroUsd(v.costMicroUsd, locale) })}</span>
                <span>{formatBytes(v.size, locale)}</span>
                {v.derivedFrom ? <span>{t("asset.derived_from", { version: v.derivedFrom.version })}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Compare({ pid, asset, versions, pair, onPair, onClose }: { pid: string; asset: Asset; versions: Version[]; pair: number[]; onPair(a: number, b: number): void; onClose(): void }) {
  const { t, locale } = useT();
  const actor = useActor(pid);
  const pick = (n: number | undefined, fallback: Version) => versions.find((v) => v.version === n) ?? fallback;
  const a = pick(pair[0], versions[1] ?? versions[0]!);
  const b = pick(pair[1], versions[0]!);
  const rows = versionDiff(a, b, { cost: (n) => formatMicroUsd(n, locale), size: (n) => formatBytes(n, locale), actor });
  const models = isModel(asset.kind, a.mediaType) && isModel(asset.kind, b.mediaType);
  const [sync] = useState(createViewSync);
  const [synced, setSynced] = useState(true);
  sync.enabled = synced;
  const sel = (side: "a" | "b") => (
    <Select
      aria-label={t(side === "a" ? "asset.compare_left" : "asset.compare_right")}
      value={side === "a" ? a.version : b.version}
      onChange={(e) => (side === "a" ? onPair(Number(e.target.value), b.version) : onPair(a.version, Number(e.target.value)))}
      data-testid={`compare-${side}`}
    >
      {versions.map((v) => (
        <option key={v.version} value={v.version}>
          {t("asset.version_short", { version: v.version })}
        </option>
      ))}
    </Select>
  );
  return (
    <section className="compare stack" data-testid="compare" aria-label={t("asset.compare")}>
      <div className="row compare__bar">
        <strong className="grow">{t("asset.compare_title")}</strong>
        {models ? (
          <label className="check compare__sync">
            <input type="checkbox" checked={synced} onChange={(e) => setSynced(e.target.checked)} data-testid="compare-sync" />
            <Icon name="link" size={14} />
            <span>{t("asset.compare_sync")}</span>
          </label>
        ) : null}
        <Button size="sm" icon="close" onClick={onClose} data-testid="compare-close">
          {t("asset.compare_exit")}
        </Button>
      </div>
      <div className="compare__panes">
        {[
          { side: "a" as const, v: a },
          { side: "b" as const, v: b },
        ].map(({ side, v }) => (
          <div key={side} className="compare__pane" data-testid={`compare-pane-${side}`}>
            {sel(side)}
            <Preview asset={asset} version={v} compact sync={models ? sync : undefined} />
          </div>
        ))}
      </div>
      <table className="compare__table small" data-testid="compare-table">
        <thead>
          <tr>
            <th scope="col">{t("asset.field")}</th>
            <th scope="col">{t("asset.version_short", { version: a.version })}</th>
            <th scope="col">{t("asset.version_short", { version: b.version })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field} className={r.changed ? "is-changed" : undefined} data-changed={r.changed}>
              <th scope="row">{t(`asset.field.${r.field}`)}</th>
              <td>{r.a}</td>
              <td>{r.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** 模型指标面板：优先 AssetVersion.model（入库解析），没有时用查看器现场计算的 */
function ModelPanel({ version, computed }: { version: Version; computed: Model3dMeta | null }) {
  const { t } = useT();
  const meta = version.model ?? computed;
  const source = version.model ? ("meta" as const) : ("computed" as const);
  return (
    <section className="card stack" aria-labelledby="mstats-title" data-testid="model-panel">
      <h2 id="mstats-title" className="section-title">
        {t("model3d.stats.title")}
      </h2>
      {meta ? <ModelStats meta={meta} source={source} /> : <p className="small muted">{t("model3d.stats.unavailable")}</p>}
    </section>
  );
}

/** 派生链路：沿 derivedFrom 往上追溯（概念图 → 模型 → 重拓扑 → 绑骨 → 动画），根在上、当前版本在下 */
function Lineage({ pid, asset, version }: { pid: string; asset: Asset; version: Version }) {
  const { t } = useT();
  if (!version.derivedFrom) return null;
  return (
    <section className="card stack" aria-labelledby="lineage-title" data-testid="lineage">
      <h2 id="lineage-title" className="section-title">
        {t("asset.lineage.title")}
      </h2>
      <ol className="lineage">
        <LineageAncestors pid={pid} from={version.derivedFrom} depth={1} seen={`${asset.id}@${version.version}`} />
        <LineageItem pid={pid} asset={asset} version={version} current />
      </ol>
    </section>
  );
}

const LINEAGE_MAX = 10;

function LineageAncestors({ pid, from, depth, seen }: { pid: string; from: NonNullable<Version["derivedFrom"]>; depth: number; seen: string }) {
  const { t } = useT();
  const q = useAsset(from.assetId);
  const key = `${from.assetId}@${from.version}`;
  const v = q.data?.versions.find((x) => x.version === from.version);
  if (q.isPending)
    return (
      <li className="lineage__item">
        <Spinner label={t("common.loading")} />
      </li>
    );
  if (!q.data || !v) return null;
  const parent = v.derivedFrom;
  // 防环：已经出现过的节点不再往上追
  const next = parent && depth < LINEAGE_MAX && !seen.split("|").includes(`${parent.assetId}@${parent.version}`) ? parent : null;
  return (
    <>
      {next ? <LineageAncestors pid={pid} from={next} depth={depth + 1} seen={`${seen}|${key}`} /> : null}
      <LineageItem pid={pid} asset={q.data} version={v} />
    </>
  );
}

function LineageItem({ pid, asset, version, current }: { pid: string; asset: Asset; version: Version; current?: boolean }) {
  const { t } = useT();
  const stage = stageOf(asset.kind, version);
  return (
    <li className={`lineage__item${current ? " is-current" : ""}`} data-testid="lineage-item" data-asset={asset.id} data-version={version.version} aria-current={current ? "step" : undefined}>
      <Link to={`/p/${pid}/assets/${asset.id}?v=${version.version}`} className="lineage__link">
        <AssetThumb asset={asset} version={version.version} hasThumb={version.hasThumb} className="lineage__thumb" />
        <span className="lineage__text">
          <span className="ellipsis">
            <strong>{asset.name}</strong> <span className="muted small">{t("asset.version_short", { version: version.version })}</span>
          </span>
          <span className="small muted">{"op" in stage ? t(`media.op.${stage.op}`) : t(`asset.kind.${stage.kind}`)}</span>
        </span>
      </Link>
    </li>
  );
}

function Feedback({ pid, asset, version }: { pid: string; asset: Asset; version: Version }) {
  const { t } = useT();
  const { api } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const errText = useErrorText();
  const agents: Agent[] = useAgents(pid).data ?? [];
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [notify, setNotify] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const agentKey = agents.map((a) => a.id).join(",");
  useEffect(() => {
    setNotify(defaultNotify(version, agents));
    // agents 变化时按 id 列表重算即可
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version.version, agentKey]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!rating && !comment.trim()) return;
    setBusy(true);
    try {
      const next = await api.call("assetFeedback", {
        params: { asid: asset.id },
        body: { version: version.version, ...(rating ? { rating } : {}), ...(comment.trim() ? { comment: comment.trim() } : {}), notify: notify as Agent["id"][] },
      });
      qc.setQueryData(ak.asset(asset.id), (cur: typeof asset | undefined) => (cur ? { ...cur, rating: next.rating } : cur));
      void qc.invalidateQueries({ queryKey: ak.all(asset.projectId) });
      toast.show({ message: notify.length ? t("asset.feedback.sent_notified", { count: notify.length }) : t("asset.feedback.sent"), tone: "success", duration: 2500 });
      setRating(0);
      setComment("");
    } catch (err) {
      toast.show({ message: errText(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card stack feedback" onSubmit={(e) => void submit(e)} data-testid="feedback-form">
      <h2 className="section-title">{t("asset.feedback.title", { version: version.version })}</h2>
      <fieldset className="plain">
        <legend className="ui-field__label">{t("asset.feedback.rating")}</legend>
        <div className="rate" role="radiogroup" aria-label={t("asset.feedback.rating")}>
          {[1, 2, 3, 4, 5].map((n) => (
            <label key={n} className={`rate__star${n <= rating ? " is-on" : ""}`} title={t("asset.feedback.stars", { count: n })}>
              <input type="radio" name="rating" value={n} checked={rating === n} onChange={() => setRating(n)} className="ui-sr-only" aria-label={t("asset.feedback.stars", { count: n })} data-testid={`rate-${n}`} />
              <Icon name="star" size={24} />
            </label>
          ))}
        </div>
      </fieldset>
      <Field label={t("asset.feedback.comment")}>
        {(id) => <TextArea id={id} rows={3} maxLength={5000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("asset.feedback.comment_placeholder")} data-testid="feedback-comment" />}
      </Field>
      {agents.length ? (
        <fieldset className="plain">
          <legend className="ui-field__label">{t("asset.feedback.notify")}</legend>
          {agents.map((a) => (
            <label key={a.id} className="check">
              <input type="checkbox" checked={notify.includes(a.id)} onChange={(e) => setNotify((xs) => (e.target.checked ? [...xs, a.id] : xs.filter((x) => x !== a.id)))} data-testid={`notify-${a.name}`} />
              <span className="grow">{a.name}</span>
              <span className="muted small">{a.role}</span>
            </label>
          ))}
          <p className="small muted">{t("asset.feedback.notify_hint")}</p>
        </fieldset>
      ) : null}
      <Button type="submit" variant="primary" loading={busy} disabled={!rating && !comment.trim()} data-testid="feedback-submit">
        {t("asset.feedback.submit")}
      </Button>
    </form>
  );
}
